import React, { useEffect, useRef, useState } from 'react';
import { Input, Label, Message, Slider } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';
import styles from './sweepSelect.module.css';
import classNames from 'classnames';

type TSweepSelect = {
    label: { id: string; defaultMessage: string };
    onChange?: (value: number) => void;
    tooltip?: React.ReactNode;
    value?: number;
    defaultValue?: number;
    slider: {
        absMin?: number;
        min: number;
        max: number;
        absMax?: number;
    };
    error?: string;
    debounce?: number;
    className?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'>;

export const SweepSelect = ({
    label,
    tooltip,
    value,
    defaultValue,
    slider,
    onChange,
    error,
    className,
    debounce,
    ...inputProps
}: TSweepSelect) => {
    const debounceRef = useRef<NodeJS.Timeout>();
    const [displayValue, setDisplayValue] = useState(defaultValue || value);
    const [outOfRange, setOutOfRange] = useState(false);

    useEffect(() => {
        value && setDisplayValue(value);
    }, [value]);

    useEffect(() => {
        if (
            displayValue &&
            (displayValue > slider.max || displayValue < slider.min)
        ) {
            setOutOfRange(true);
        } else if (outOfRange) {
            setOutOfRange(false);
        }
    }, [displayValue, outOfRange, setOutOfRange, slider]);

    const changeValue = (ev: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(ev.target.value);
        setDisplayValue(value);

        if (onChange) {
            if (debounce) {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                }
                debounceRef.current = setTimeout(() => {
                    onChange(value);
                }, debounce);
            } else {
                onChange(value);
            }
        }
    };

    return (
        <div className={classNames(styles.amountSelect, className)}>
            <div className={styles.input}>
                <Label className={styles.label}>
                    <FormattedMessage
                        {...label}
                        values={{
                            input: (
                                <Input
                                    wrapperClassName={styles.inputWrapper}
                                    className={classNames(styles.sweepInput, {
                                        [styles.error]: outOfRange
                                    })}
                                    value={displayValue || ''}
                                    onChange={changeValue}
                                    type="number"
                                    step={0.01}
                                    iconRight="percent"
                                    {...inputProps}
                                />
                            )
                        }}
                    />
                </Label>
                {tooltip}
            </div>
            <div className={styles.slider}>
                <Slider
                    leftLabel={`${slider.absMin || slider.min}%`}
                    rightLabel={`${slider.absMax || slider.max}%`}
                    absMin={slider.absMin}
                    min={slider.min}
                    max={slider.max}
                    absMax={slider.absMax}
                    stops
                    step={0.01}
                    value={displayValue}
                    onChange={changeValue}
                />
            </div>
            {error && (
                <Message
                    className={styles.errorMessage}
                    type="error"
                    message={error}
                />
            )}
        </div>
    );
};
